/**
 * Roles that represent the person who actually owns (or will own) the
 * underlying property in the transaction. Used to decide whether a "home"
 * affordance (e.g. the property-selector pill's house icon) is meaningful
 * for the signed-in participant. Anyone else on the deal — agent, attorney,
 * lender, title company, etc. — never owns the property, so the house icon
 * would be visual noise.
 *
 * Values mirror the canonical ROLE_OPTIONS in the staff portal's
 * src/constants/newDeal.constants.js. Comparison is case-insensitive
 * to defend against any drift in casing across services.
 */
const PROPERTY_OWNER_ROLES = new Set(['buyer', 'seller']);

export const isPropertyOwnerRole = (role: string | null | undefined): boolean => {
    if (!role) return false;
    return PROPERTY_OWNER_ROLES.has(role.trim().toLowerCase());
};

/**
 * Roles allowed to start a payment they were not assigned a task for.
 *
 * Buyer only, for now. Buyer is the overwhelming majority of participants
 * production has ever asked for a digital payment, and sellers are excluded on
 * purpose rather than for volume: they are the receiving side of the money, so
 * offering them earnest money or cash to close is wrong, not merely rare.
 *
 * Normalization is stricter than isPropertyOwnerRole's trim + lowercase. Role
 * strings reach us from staff-entered data with casing and spacing drift, so
 * every non-alphanumeric character is stripped before comparison; a plain
 * lowercase comparison silently drops a real share of buyers.
 */
const PAYMENT_INITIATOR_ROLES = new Set(['buyer']);

export const isPaymentInitiatorRole = (role: string | null | undefined): boolean => {
    if (!role) return false;
    return PAYMENT_INITIATOR_ROLES.has(role.toLowerCase().replace(/[^a-z0-9]/g, ''));
};
